import { Program } from '@citruscamps/citrus-client'
import React from 'react'
import { IDateMacroQuery } from '../../interfaces/DateMacroQuery'
import {
  filterStepsByDuration,
  getIntervalByDateMacro,
  getIntervalByQuery,
} from '../../utils/date-macro'
import { UpgradeMessage } from '../FeatureFlag/components/UpgradeMessage'
import { isHistoryViewValid } from '../FeatureFlag/utils/isHistoryViewValid'
import { usePopper } from '../Popover/hooks/usePopper'
import { TooltipPopover } from '../Tooltip/Tooltip'
import { DateSelectContainer, isAnalyticsStep } from './components/DateSelectContainer'
import { useDateQueryWarning } from './hooks/useDateQueryWarning'

interface IProps extends React.HTMLAttributes<HTMLDivElement> {
  values: IDateMacroQuery
  program: Program
  hideStep?: boolean
  transformOnSelect?: boolean
  onSelectMacro: (value: IDateMacroQuery) => any
}

export const DateMacroSelect = ({
  values,
  program,
  hideStep = false,
  transformOnSelect = true,
  onSelectMacro: handleSelect,
  ...props
}: IProps) => {
  const { isWarningShown, transform: _transform } = useDateQueryWarning({ program })
  const transform = transformOnSelect ? _transform : (val: IDateMacroQuery) => val
  const interval = transformOnSelect
    ? getIntervalByQuery(values, program)
    : getIntervalByDateMacro(values, program)
  return (
    <div className={['row justify-content-between', props.className].join(' ').trim()} {...props}>
      <div className="mb-3 col-12 col-lg-auto mx-md-0 mx-auto">
        <DateSelectContainer
          values={values}
          interval={interval}
          program={program}
          onSelect={(val: IDateMacroQuery) => {
            handleSelect(transform(val))
          }}
        />
      </div>
      {!hideStep && (
        <div className="mb-3 col-12 col-lg-auto mx-md-0 mx-auto">
          <DateMacroWarning isShown={isWarningShown}>
            <select
              value={values.unit}
              className="form-select form-select-sm"
              onChange={(e) => {
                if (!isAnalyticsStep(e?.target?.value)) {
                  return
                }
                const dateMacroQuery: IDateMacroQuery = {
                  date_macro: values.date_macro,
                  unit: e?.target?.value,
                  start_date: values.start_date,
                  end_date: values.end_date,
                  timezone: values.timezone,
                }
                handleSelect(transform(dateMacroQuery))
              }}
            >
              {filterStepsByDuration(getIntervalByDateMacro(values, program).toDuration()).map(
                (value: string) => (
                  <option key={value} value={value} className="text-capitalize">
                    {value[0].toUpperCase() +
                      value
                        .slice(1)
                        .toLowerCase()
                        .replace(/[^a-z0-9]/gim, ' ')}
                  </option>
                ),
              )}
            </select>
          </DateMacroWarning>
        </div>
      )}
      <div className="col-12">
        <UpgradeMessage
          flagKey={[]}
          checkAll={(flags) =>
            !!interval.start && isHistoryViewValid(interval.start.toJSDate(), flags)
          }
          feature="unlimited analytics"
          product="standard"
        />
      </div>
    </div>
  )
}

interface IDateMacroWarningProps extends React.PropsWithChildren<{}> {
  isShown: boolean
}

const DateMacroWarning = ({ isShown, children }: IDateMacroWarningProps) => {
  const { target, targetProps, toggle, ...tooltipProps } = usePopper<HTMLSpanElement>({
    open: isShown,
    roleType: 'tooltip',
    placement: window.innerWidth >= 992 ? 'top' : 'bottom',
  })
  return (
    <>
      <span ref={target} {...targetProps}>
        {children}
      </span>
      <TooltipPopover {...tooltipProps}>
        <div className="tooltip">The selected dates are rounded to match this unit.</div>
      </TooltipPopover>
    </>
  )
}
